import { clientCreateSchema, clientUpdateSchema, phoneSearchDigits, type ClientDto } from '@kominiarz/shared';
import { Plus, Save, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { AddressField } from '../../components/AddressField';
import { Alert } from '../../components/Alert';
import { OfflineNotice } from '../../components/OfflineNotice';
import { useOnline } from '../../lib/appStatus';
import { Button } from '../../components/Button';
import { TextArea } from '../../components/form/TextArea';
import { TextInput } from '../../components/form/TextInput';
import { Spinner } from '../../components/Loading';
import { PageBody } from '../../components/PageBody';
import { PageHeader } from '../../components/PageHeader';
import { Section } from '../../components/Section';
import { SimilarClients } from '../../components/SimilarClients';
import { useToast } from '../../components/Toast';
import { ApiError, errorMessage } from '../../lib/api';
import { useClient, useCreateClient, useUpdateClient } from '../../lib/clientsApi';
import styles from './clients.module.css';

interface PhoneRow {
  phone: string;
  label: string;
}

interface FormValues {
  name: string;
  email: string;
  note: string;
  phones: PhoneRow[];
  /** New client only: the first address. */
  objectLabel: string;
  objectAddress: string;
  objectNote: string;
  objectLat: number | null;
  objectLng: number | null;
}

function issuesToFields(issues: Array<{ path: PropertyKey[]; message: string }>) {
  const fields: Record<string, string> = {};
  for (const i of issues) fields[i.path.join('.')] ??= i.message;
  return fields;
}

export function NewClientPage() {
  const [params] = useSearchParams();
  const typed = params.get('nazwa') ?? '';
  const isPhone = phoneSearchDigits(typed).length >= 6;
  return (
    <ClientForm
      initial={{
        name: isPhone ? '' : typed,
        email: '',
        note: '',
        phones: [{ phone: isPhone ? typed : '', label: '' }],
        objectLabel: '',
        objectAddress: '',
        objectNote: '',
        objectLat: null,
        objectLng: null,
      }}
    />
  );
}

export function EditClientPage() {
  const id = Number(useParams().id);
  const query = useClient(Number.isFinite(id) ? id : null);
  if (query.isPending || query.isError) {
    return (
      <>
        <PageHeader title="Edycja klienta" back={`/klienci/${id}`} />
        <PageBody>{query.isPending ? <Spinner /> : <Alert kind="error">{errorMessage(query.error)}</Alert>}</PageBody>
      </>
    );
  }
  const c = query.data.client;
  if (!c.permissions.edit) {
    return (
      <>
        <PageHeader title="Edycja klienta" back={`/klienci/${id}`} />
        <PageBody>
          <Alert kind="error">Dane tego klienta może zmienić kierownik albo osoba, która go dodała.</Alert>
        </PageBody>
      </>
    );
  }
  return (
    <ClientForm
      client={c}
      initial={{
        name: c.name ?? '',
        email: c.email ?? '',
        note: c.note ?? '',
        phones: c.phones.length ? c.phones.map((p) => ({ phone: p.phone, label: p.label ?? '' })) : [{ phone: '', label: '' }],
        objectLabel: '',
        objectAddress: '',
        objectNote: '',
        objectLat: null,
        objectLng: null,
      }}
    />
  );
}

function ClientForm({ initial, client }: { initial: FormValues; client?: ClientDto }) {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateClient();
  const update = useUpdateClient(client?.id ?? 0);
  const [v, setValues] = useState<FormValues>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);

  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) => setValues((prev) => ({ ...prev, [key]: value }));
  const setPhone = (i: number, patch: Partial<PhoneRow>) =>
    setValues((prev) => ({ ...prev, phones: prev.phones.map((p, j) => (j === i ? { ...p, ...patch } : p)) }));

  async function submit(e: FormEvent) {
    e.preventDefault();
    setFailure(null);
    const phones = v.phones.filter((p) => p.phone.trim()).map((p) => ({ phone: p.phone, label: p.label }));
    const body = {
      name: v.name,
      email: v.email,
      note: v.note,
      phones,
      ...(client
        ? {}
        : {
            objects: v.objectAddress.trim()
              ? [{ label: v.objectLabel, address: v.objectAddress, note: v.objectNote, lat: v.objectLat, lng: v.objectLng }]
              : [],
          }),
    };
    const parsed = client ? clientUpdateSchema.safeParse(body) : clientCreateSchema.safeParse(body);
    if (!parsed.success) {
      setErrors(issuesToFields(parsed.error.issues));
      setFailure('Popraw zaznaczone pola.');
      return;
    }
    setErrors({});
    try {
      const res = client
        ? await update.mutateAsync(parsed.data)
        : await create.mutateAsync(parsed.data as Parameters<typeof create.mutateAsync>[0]);
      toast.success(client ? 'Zapisano zmiany' : 'Klient dodany');
      navigate(`/klienci/${res.client.id}`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length) {
        setErrors(err.fields);
        setFailure('Popraw zaznaczone pola.');
      } else {
        setFailure(errorMessage(err));
      }
    }
  }

  // Phone errors refer to the filtered list; map them back to the visible rows.
  const filledIndex = v.phones.map((p, i) => (p.phone.trim() ? i : -1)).filter((i) => i >= 0);
  const phoneError = (row: number) => {
    const pos = filledIndex.indexOf(row);
    return pos >= 0 ? errors[`phones.${pos}.phone`] : undefined;
  };

  const saving = create.isPending || update.isPending;
  const online = useOnline();
  return (
    <>
      <PageHeader title={client ? 'Edycja klienta' : 'Nowy klient'} back={client ? `/klienci/${client.id}` : '/klienci'} />
      <PageBody>
        <form className={styles.form} onSubmit={submit} noValidate>
          <OfflineNotice />
          <Section title="Dane klienta">
            <TextInput
              label="Imię i nazwisko albo nazwa firmy"
              hint="Można pominąć, jeśli jest telefon – klient będzie widoczny pod numerem."
              autoComplete="off"
              value={v.name}
              onChange={(e) => set('name', e.target.value)}
              error={errors.name}
            />
            {v.phones.map((p, i) => (
              <div key={i} className={styles.phoneRow}>
                <TextInput
                  type="tel"
                  inputMode="tel"
                  label={v.phones.length > 1 ? `Telefon ${i + 1}` : 'Telefon'}
                  value={p.phone}
                  onChange={(e) => setPhone(i, { phone: e.target.value })}
                  error={phoneError(i)}
                />
                <TextInput
                  label="Czyj to numer"
                  optional
                  placeholder="np. córka, biuro"
                  value={p.label}
                  onChange={(e) => setPhone(i, { label: e.target.value })}
                />
                {v.phones.length > 1 && (
                  <Button
                    variant="ghost"
                    icon={<Trash2 aria-hidden="true" />}
                    onClick={() => set('phones', v.phones.filter((_, j) => j !== i))}
                  >
                    Usuń numer
                  </Button>
                )}
              </div>
            ))}
            {!client && (
              <SimilarClients
                name={v.name}
                phone={v.phones[0]?.phone ?? ''}
                action={(c) => <Link to={`/klienci/${c.id}`}>Otwórz</Link>}
              />
            )}
            {v.phones.length < 5 && (
              <div>
                <Button variant="secondary" icon={<Plus aria-hidden="true" />} onClick={() => set('phones', [...v.phones, { phone: '', label: '' }])}>
                  Dodaj numer
                </Button>
              </div>
            )}
            <TextInput
              type="email"
              label="E-mail"
              optional
              inputMode="email"
              autoComplete="off"
              value={v.email}
              onChange={(e) => set('email', e.target.value)}
              error={errors.email}
            />
            <TextArea label="Notatka" optional value={v.note} onChange={(e) => set('note', e.target.value)} error={errors.note} />
          </Section>

          {!client && (
            <Section title="Adres" description="Opcjonalnie. Kolejne adresy dodasz na karcie klienta.">
              <TextInput
                label="Nazwa"
                optional
                placeholder="np. Dom, Garaż, Kamienica"
                value={v.objectLabel}
                onChange={(e) => set('objectLabel', e.target.value)}
              />
              <AddressField
                label="Adres"
                value={v.objectAddress}
                coords={v.objectLat != null && v.objectLng != null ? { lat: v.objectLat, lng: v.objectLng } : null}
                onType={(a) => setValues((prev) => ({ ...prev, objectAddress: a, objectLat: null, objectLng: null }))}
                onPick={(a, c) => setValues((prev) => ({ ...prev, objectAddress: a, objectLat: c.lat, objectLng: c.lng }))}
                onPin={(c) => setValues((prev) => ({ ...prev, objectLat: c.lat, objectLng: c.lng }))}
                error={errors['objects.0.address']}
              />
              <TextArea
                label="Uwagi do adresu"
                optional
                rows={2}
                placeholder="np. kod do bramy, pies, piec gazowy w piwnicy"
                value={v.objectNote}
                onChange={(e) => set('objectNote', e.target.value)}
              />
            </Section>
          )}

          {failure && <Alert kind="error">{failure}</Alert>}
          <div className={styles.formButtons}>
            <Button type="submit" variant="primary" size="lg" icon={<Save aria-hidden="true" />} disabled={saving || !online}>
              {saving ? 'Zapisywanie…' : client ? 'Zapisz zmiany' : 'Dodaj klienta'}
            </Button>
            <Button variant="secondary" size="lg" onClick={() => navigate(client ? `/klienci/${client.id}` : '/klienci')}>
              Anuluj
            </Button>
          </div>
        </form>
      </PageBody>
    </>
  );
}
